import { createApi } from '@reduxjs/toolkit/query/react';
import { saveAs } from 'file-saver';
import { createBaseQuery, toQueryParams } from 'helpers/api';
import { stringDate } from 'helpers/date';

export const companyApi = createApi({
  reducerPath: 'companyApi',
  baseQuery: createBaseQuery('companies', { timeout: 0 }),
  endpoints: builder => ({
    getCompanyUsers: builder.query({
      query: ({ id, params }) => `/${id}/users?${toQueryParams(params)}`,
    }),
    getCompany: builder.query({
      query: id => `/${id}`,
      transformResponse: ({ result }) => result,
    }),
    getCompanyTeams: builder.query({
      query: id => `/${id}/teams`,
      transformResponse: ({ result }) => result,
    }),
    createCompanyOnBehalf: builder.mutation({
      query: data => ({
        url: '/on_behalf',
        method: 'POST',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    updateCompany: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    getCompanyChecks: builder.query({
      query: id => `/${id}/company_checks`,
      transformResponse: ({ result }) => result,
    }),
    getCompanyOwner: builder.query({
      query: id => `/${id}/owner`,
      transformResponse: ({ result }) => result,
    }),
    getCompanies: builder.query({
      query: params => `/?${toQueryParams(params)}`,
    }),
    getAllCompaniesList: builder.query({
      query: () => '/list',
    }),
    getCompanyBilling: builder.query({
      query: id => `/${id}/billing`,
      transformResponse: ({ result }) => result,
    }),
    updateCompanyBilling: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}/billing`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
    }),
    getCompanyUsageReport: builder.query({
      queryFn: async ({ id, data }, api, extraOptions, baseQuery) => {
        const { data: file } = await baseQuery({
          url: `/${id}/reports/usage?${toQueryParams(data)}`,
          responseHandler: async response => {
            const blob = await response.blob();
            return blob;
          },
          cache: 'no-cache',
        });
        saveAs(file, `${id}_usage_report_${stringDate()}`);
        return { data: null };
      },
    }),
    getCompanyBillingReport: builder.query({
      queryFn: async ({ id, data }, api, extraOptions, baseQuery) => {
        const { data: file, error } = await baseQuery({
          url: `/${id}/reports/billing_report?${toQueryParams(data)}`,
          responseHandler: async response => response.blob(),
          cache: 'no-cache',
        });
        if (error) return { error };
        saveAs(file, `${id}_billing_report_${stringDate()}.csv`);
        return { data: null };
      },
    }),
  }),
});

export const {
  useCreateCompanyOnBehalfMutation,
  useGetCompanyBillingQuery,
  useLazyGetCompanyUsageReportQuery,
  useLazyGetCompanyBillingReportQuery,
  useUpdateCompanyMutation,
  useUpdateCompanyBillingMutation,
  useGetCompanyChecksQuery,
  useGetCompanyQuery,
  useGetCompanyTeamsQuery,
  useGetCompanyOwnerQuery,
  useLazyGetCompaniesQuery,
  useGetCompanyUsersQuery,
  useLazyGetCompanyUsersQuery,
  useGetAllCompaniesListQuery,
} = companyApi;
